import { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '@/services/api';
import { Mail, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import AuthLayout, { themes } from '@/components/layout/AuthLayout';
import { useTheme } from '@/hooks/useTheme';

function useFormStyles() {
  const { theme } = useTheme();
  const t = themes[theme] || themes.dark;
  return {
    input: {
      width: '100%', padding: '13px 16px', borderRadius: '12px', border: `1.5px solid ${t.inputBorder}`,
      background: t.inputBg, color: t.text, fontSize: '14px', fontFamily: "'Inter',sans-serif",
      transition: 'all .25s', outline: 'none',
    },
    inputFocus: {
      borderColor: t.inputFocus, background: t.surface2, boxShadow: `0 0 0 3px ${t.surface}`,
    },
    label: {
      display: 'block', fontSize: '13px', fontWeight: 600, color: t.text2, marginBottom: '6px',
    },
    btnPrimary: {
      background: t.gradBtn, color: t.bg, fontWeight: 600, position: 'relative', overflow: 'hidden',
      transition: 'all .3s cubic-bezier(.16,1,.3,1)', cursor: 'pointer', border: 'none',
      fontFamily: "'Inter',sans-serif",
    },
    t,
  };
}

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const styles = useFormStyles();
  const { t } = styles;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email');
      return;
    }
    setLoading(true);
    try {
      await authAPI.forgotPassword({ email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const rightPanel = sent ? (
    <div className="text-center" style={{ animation: 'fadeUp .7s .2s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 border-2" style={{ background: t.surface2, borderColor: t.success }}>
        <CheckCircle className="w-7 h-7" style={{ color: t.success }} />
      </div>
      <h3 className="text-lg font-bold mb-2" style={{ color: t.text }}>Check your inbox</h3>
      <p className="text-sm mb-6" style={{ color: t.text3 }}>We&apos;ve sent a password reset link to <strong style={{ color: t.text }}>{email}</strong></p>
      <Link to="/login" className="btn-p inline-flex items-center gap-2 px-8 py-3 rounded-xl text-sm" style={styles.btnPrimary}>
        <ArrowLeft className="w-4 h-4" /> Back to login
      </Link>
    </div>
  ) : (
    <>
      <div className="mb-8" style={{ animation: 'fadeUp .7s .2s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
        <h2 className="text-2xl font-bold mb-1.5" style={{ color: t.text }}>Reset your password</h2>
        <p className="text-sm" style={{ color: t.text3 }}>Enter your email and we&apos;ll send you a reset link.</p>
      </div>

      <form onSubmit={handleSubmit} noValidate style={{ animation: 'fadeUp .7s .3s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
        <div className="mb-4">
          <label className="form-label" style={styles.label}>Email address</label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: t.text4 }} />
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="form-input" style={{ ...styles.input, paddingLeft: '40px' }}
              placeholder="you@company.com" autoComplete="email"
              onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
              onBlur={(e) => { e.target.style.borderColor = error ? t.error : t.inputBorder; e.target.style.background = t.inputBg; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          {error && <div className="flex items-center gap-1 mt-1 text-xs" style={{ color: t.error }}><span>!</span><span>{error}</span></div>}
        </div>

        <button type="submit" disabled={loading} className="btn-p w-full py-3.5 rounded-xl text-sm flex items-center justify-center" style={styles.btnPrimary}>
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending…</> : 'Send reset link'}
        </button>
      </form>

      <p className="text-center text-sm mt-8" style={{ color: t.text3, animation: 'fadeUp .7s .4s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
        Remember your password? <Link to="/login" className="font-semibold hover:opacity-70 transition-opacity" style={{ color: t.text, textDecoration: 'none' }}>Log in</Link>
      </p>
    </>
  );

  return <AuthLayout rightPanel={rightPanel} />;
}
